import React, { useEffect, useState } from 'react';
import { Box, Grid, Avatar } from '@mui/material';
import ButtonLabel from '@src/new-components/Button/ButtonLabel';
import { COLOR } from '@src/styles/color';
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import { useTranslation } from 'react-i18next';
import useAgeGroupVoiceCloning from '@src/hooks/useAgeGroupVoiceCloning';
import CustomTypography from '@src/new-components/Typography';
import { Edit } from '@mui/icons-material';
import LimitedInput from '@src/new-components/Input/LimitedInput';
import Select from '@src/new-components/Select';
import Title from './Title';
import PopupChangeVoice from '../PopupChangeVoice';

const AvatarEdit = ({ avatar, handleAvatarChange }) => (
  <Box className="avatar-wrapper">
    <label htmlFor="avatar-input">
      <Avatar src={avatar} alt="avatar" className="avatar" />
      <input
        type="file"
        accept="image/*"
        id="avatar-input"
        style={{ display: 'none' }}
        onChange={handleAvatarChange}
      />
      <Box className="edit-icon">
        <Edit className="icon" />
      </Box>
    </label>
  </Box>
);

const FormDataInfoVoice = ({
  handleCloseDialog,
  avatar,
  voiceInfo,
  handleSelect,
  error,
  handleAvatarChange,
  voiceCloningInTTS,
  voice,
  selectedSampleVoice,
  setSelectedSampleVoice,
}) => {
  const { t } = useTranslation();
  const { ageGroup } = useAgeGroupVoiceCloning();
  const [openPopupSampleAudio, setOpenPopupSampleAudio] = useState(false);
  const [sampleAudios, setSampleAudios] = useState([]);
  const [draftSampleVoice, setDraftSampleVoice] = useState(null);

  useEffect(() => {
    const sampleAudiosFromTTS = voiceCloningInTTS?.sampleAudios || [];
    const sampleAudiosFromVoiceCloning = voice?.sampleVoices || [];

    const mergedSampleAudios = [
      ...sampleAudiosFromVoiceCloning,
      ...sampleAudiosFromTTS,
    ];
    const formattedSampleAudios = mergedSampleAudios.map((item) => ({
      ...item,
      audioLink: item?.audioLink || item?.sampleLink,
      text: item?.text || item?.sampleText,
    }));
    setSampleAudios(formattedSampleAudios);

    const selectedSampleAudio = formattedSampleAudios.find(
      (item) => item.audioLink === voiceCloningInTTS?.demo,
    );
    setSelectedSampleVoice(selectedSampleAudio || formattedSampleAudios[0]);
  }, [voiceCloningInTTS, voice]);

  return (
    <>
      <Title title={t('shareAndEarn')} handleCloseDialog={handleCloseDialog} />
      <AvatarEdit avatar={avatar} handleAvatarChange={handleAvatarChange} />
      <Grid container spacing={2} mb={3} mt={0}>
        <Grid item xs={12} sm={12}>
          <ButtonLabel
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              border: `1px solid ${COLOR.borderButtonCorlor}`,
              width: '100%',
              justifyContent: 'space-between',
              padding: '0 16px',
              alignItems: 'center',
              gap: '8px',
              '&:focus': {
                backgroundColor: 'transparent',
              },
              '&:hover': {
                border: `1px solid ${COLOR.borderButtonCorlor}`,
                opacity: 1,
              },
              '&:disabled': {
                backgroundColor: COLOR.backgroundRecordTime,
                opacity: 1,
                cursor: 'not-allowed',
                pointerEvents: 'auto',
              },
            }}
            onClick={() => setOpenPopupSampleAudio(true)}
            variant="outlined"
            label={
              <CustomTypography variant="body-14-medium" color={COLOR.darkGray}>
                {t('sampleAudio')}
              </CustomTypography>
            }
          >
            <CustomTypography variant="body-14-regular" color={COLOR.darkGray}>
              Audio{' '}
              {sampleAudios?.findIndex(
                (item) => item.id === selectedSampleVoice?.id,
              ) + 1}
            </CustomTypography>
            <ArrowRightRoundedIcon
              sx={{ color: COLOR.indigo[80], fontSize: '24px' }}
            />
          </ButtonLabel>
        </Grid>

        <Grid item xs={12} sm={6}>
          <LimitedInput
            label={t('voiceName')}
            name="voiceName"
            value={voiceInfo.voiceName}
            placeholder={t('enterVoiceName')}
            disabled={false}
            onChange={handleSelect}
            error={error.voiceName}
            showCounter={false}
            helperText={error.voiceName}
            multiline={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            label={t('ageGroup')}
            name="ageGroup"
            value={Object.values(ageGroup).find(
              (option) => option === voiceInfo.ageGroup,
            )}
            placeholder={t('placeHolderGroupAge')}
            disabled={false}
            onChange={handleSelect}
            options={Object.values(ageGroup)}
            fullWidth
            getOptionLabel={(option) => t(option)}
            getOptionValue={(option) => option}
            error={error.ageGroup}
            helperText={error.ageGroup}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <LimitedInput
            label={t('description')}
            name="description"
            value={voiceInfo.description}
            placeholder={t('placeHolderDescription')}
            disabled={false}
            onChange={handleSelect}
            error={error.description}
            helperText={error.description}
            rows={5}
            maxLength={200}
            multiline
          />
        </Grid>
      </Grid>
      <PopupChangeVoice
        sampleAudios={sampleAudios}
        openPopupSampleAudio={openPopupSampleAudio}
        setOpenPopupSampleAudio={setOpenPopupSampleAudio}
        selectedSampleVoice={selectedSampleVoice}
        setSelectedSampleVoice={setSelectedSampleVoice}
        draftSampleVoice={draftSampleVoice}
        setDraftSampleVoice={setDraftSampleVoice}
      />
    </>
  );
};

export default FormDataInfoVoice;
